import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Flex,
    Button,
    Text,
    Image,
    Drawer,
    DrawerHeader,
    DrawerBody,
    DrawerFooter,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useDisclosure,
    Box,
    Icon
} from '@salesforce/retail-react-app/app/components/shared/ui';


import { useBreakpointValue } from '@chakra-ui/react';

import { FormattedMessage, useIntl } from 'react-intl';
import { HideOnMobile } from '@salesforce/retail-react-app/app/components/responsive';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import {review} from '../../../integrations/bazarvoice/hooks/use-bazarvoice'
import Link from '@salesforce/retail-react-app/app/components/link'
import withRegistration from '@salesforce/retail-react-app/app/components/with-registration'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'

import {sendSubmitReviewEvent, sendViewErrorEvent} from '../../../utils/gtm-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../utils/site-config'
import {useSitePreferences, preferenceToBool} from '../../../hooks/use-site-preferences'
import {AuthModal, useAuthModal} from '../../../hooks/use-auth-modal'
import {useBrand} from '../../../contexts/brand-context'

/* Forms */
import WriteReviewFields from '../../forms/write-review-fields'

/* Icons */
import { ChevronLeftIcon, CustomClose, CheckCircleIcon } from '../../icons';

/* Styles */
import {productThumbnailStyles, writeReviewCollectifBannerStyles} from './write-review.styles'

/* Constants */
const {app: appConfig} = getConfig()
const TRANSFORMATION_PARAMS = '?sw=100&q=75'

const LinkWithRegistration = withRegistration(Link)

const WriteReview = ({
    reviewFormData,
    images,
    isOpen,
    onClose,
    onOpenWriteReview,
    product,
    writeReviewButtonRef
}) => {
    const {formatMessage} = useIntl()
    const {isGarage} = useBrand()
    const {site} = useMultiSite()
    const siteId = site?.id
    const PRODUCT_IMGS_RESIZE_PARAMS_ENABLED = getSiteEnvVarValue(appConfig, 'PRODUCT_IMGS_RESIZE_PARAMS_ENABLED', siteId) == 'true'
    const form = useForm({
        mode: 'onSubmit',
        shouldFocusError: false // Disable auto-focus on first error; scrollToFirstError handles drawer-aware scrolling
    });

    const { onOpen } = useDisclosure()
    const {data: customer} = useCurrentCustomer()
    const isRegistered = !!customer?.isRegistered
    const {data: pointsPrefs} = useSitePreferences(['enablePointsForReview'])
    const enablePointsForReview = preferenceToBool(pointsPrefs?.enablePointsForReview)
    const authModal = useAuthModal()
    const scrollPositionBeforeAuthRef = useRef(null)
    const pdpPathBeforeAuthRef = useRef(null)
    const shouldResumeWriteReviewRef = useRef(false)

    const [isReviewSubmittedOpen, setReviewSubmittedOpen] = useState(false);
    const [isCloseDrawerOpen, setCloseDrawerOpen] = useState(false);
    const [wasCancelReviewClicked, setWasCancelReviewClicked] = useState(false); // New state variable

    const [globalError, setGlobalError] = useState(null);

    const drawerBodyRef = useRef(null)
    const submitFocusSentinelRef = useRef(null)

    const moveFocusAwayFromSubmit = useCallback(() => {
        submitFocusSentinelRef.current?.focus?.({preventScroll: true})
    }, [])

    const mapErrorKeyToFieldId = useCallback(
        (errorKey) => {
            if (!errorKey) return null

            const normalizedErrorKey = String(errorKey).toLowerCase()
            const directFieldId = reviewFormData?.[errorKey]?.Id || reviewFormData?.[normalizedErrorKey]?.Id
            if (directFieldId) {
                return directFieldId
            }

            const matchingField = Object.values(reviewFormData || {}).find((field) => {
                const fieldId = field?.Id
                return fieldId && String(fieldId).toLowerCase() === normalizedErrorKey
            })

            return matchingField?.Id || errorKey
        },
        [reviewFormData]
    )

    const scrollToElementInDrawer = useCallback((targetElement) => {
        const drawerBody = drawerBodyRef.current
        if (!drawerBody || !targetElement) return

        const drawerRect = drawerBody.getBoundingClientRect()
        const elementRect = targetElement.getBoundingClientRect()
        const offset =
            elementRect.top -
            drawerRect.top +
            drawerBody.scrollTop -
            (drawerBody.clientHeight / 2 - elementRect.height / 2)

        drawerBody.scrollTo({top: offset, behavior: 'smooth'})
    }, [])

    const scrollToServerFieldError = useCallback(
        (fieldErrorKeys = []) => {
            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    const drawerBody = drawerBodyRef.current
                    if (!drawerBody) return

                    // Walk up from an element to find the closest Chakra FormControl
                    // ancestor that has data-invalid set, then prefer the visible
                    // role="alert" error message inside it (critical for hidden inputs
                    // which have no visual position — we must scroll to their error text).
                    const getScrollTarget = (el) => {
                        let node = el?.parentElement
                        while (node && node !== drawerBody) {
                            if (node.hasAttribute('data-invalid')) {
                                // Scroll to the visible error message text rather than
                                // the field itself, so the user can see what went wrong
                                const errorMsg = node.querySelector('[role="alert"]')
                                return errorMsg || node
                            }
                            node = node.parentElement
                        }
                        // No data-invalid ancestor found: try a sibling/nearby role=alert
                        const parent = el?.parentElement
                        if (parent) {
                            const siblingAlert = parent.querySelector('[role="alert"]')
                            if (siblingAlert) return siblingAlert
                        }
                        return el
                    }

                    const firstMatchingErrorField = fieldErrorKeys
                        .map(mapErrorKeyToFieldId)
                        .filter(Boolean)
                        .map((fieldId) => {
                            const escapedId = String(fieldId).replace(/["\\]/g, '\\$&')
                            // Match native inputs, custom components by name attr, or by id
                            const el = drawerBody.querySelector(
                                `input[name="${escapedId}"], textarea[name="${escapedId}"], select[name="${escapedId}"], [name="${escapedId}"], #${escapedId}`
                            )
                            return el ? getScrollTarget(el) : null
                        })
                        .find(Boolean)

                    if (firstMatchingErrorField) {
                        scrollToElementInDrawer(firstMatchingErrorField)
                        return
                    }

                    // Fallback: prefer a visible error message inside an invalid wrapper,
                    // then fall back to any invalid wrapper or aria-invalid element
                    const firstVisibleError =
                        drawerBody.querySelector('[data-invalid] [role="alert"]') ||
                        drawerBody.querySelector('[aria-invalid="true"], [data-invalid]')
                    if (firstVisibleError) {
                        scrollToElementInDrawer(firstVisibleError)
                    }
                })
            })
        },
        [mapErrorKeyToFieldId, scrollToElementInDrawer]
    )

    const scrollToFirstError = () => {
        // Double RAF ensures React has committed the re-render with aria-invalid
        // attributes before we query the DOM for the first invalid element.
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                const drawerBody = drawerBodyRef.current
                if (!drawerBody) return
                // Prefer the visible error message text inside an invalid wrapper,
                // then fall back to a standalone role=alert (e.g. hidden input errors),
                // then fall back to any invalid wrapper or aria-invalid element
                const firstVisibleError =
                    drawerBody.querySelector('[data-invalid] [role="alert"]') ||
                    drawerBody.querySelector('[role="alert"]') ||
                    drawerBody.querySelector('[aria-invalid="true"], [data-invalid]')
                if (firstVisibleError) {
                    scrollToElementInDrawer(firstVisibleError)
                }
            })
        })
    }

    const drawerPlacement = useBreakpointValue({ base: 'bottom', md: 'right' });

    const {submitReview} = review();

    var pid = product?.id

    if (product?.type?.variant) {
        const matchingGroup = product?.variationGroups?.find(
            (group) => group?.variationValues?.color === product?.variationValues?.color
        )

        if (matchingGroup) {
            pid = matchingGroup.productId
        }
    }

    const onSubmit = async (data) => {
        // Save scroll position so errors/re-renders don't reset the drawer position
        const savedScrollTop = drawerBodyRef.current?.scrollTop ?? 0
        moveFocusAwayFromSubmit()

        try {
            const result = await submitReview({pid, formData: JSON.stringify(data)})
            if (result?.HasErrors) {
                const fieldErrors = result?.FormErrors?.FieldErrors || {}
                const errorKeys = Object.keys(fieldErrors)

                errorKeys.forEach((key) => {
                    const errorMsg = fieldErrors[key]?.Message
                    const mappedFieldId = mapErrorKeyToFieldId(key)
                    // GTM Modification
                    sendViewErrorEvent(errorMsg)
                    form.setError(mappedFieldId, {
                        type: 'manual',
                        message: errorMsg
                    })
                })
                // Restore scroll before scrolling to the first server error
                if (drawerBodyRef.current) drawerBodyRef.current.scrollTop = savedScrollTop
                scrollToServerFieldError(errorKeys)
                return
            } else if (!result || result.hasError) {
                if (drawerBodyRef.current) drawerBodyRef.current.scrollTop = savedScrollTop
                setGlobalError('Failed to submit data');
                return;
            }

            // GTM Modification
            sendSubmitReviewEvent(product, data, result?.SubmissionId)

            form.reset()
            setGlobalError(null)
            setReviewSubmittedOpen(true); // Open the review submitted modal
        } catch (error) {
            if (drawerBodyRef.current) drawerBodyRef.current.scrollTop = savedScrollTop
            setGlobalError(error.message)
        }
    };

    const getImageUrl = useCallback((image) => {
        const baseUrl = image.disBaseLink || image.link
        return PRODUCT_IMGS_RESIZE_PARAMS_ENABLED
            ? `${baseUrl}${TRANSFORMATION_PARAMS}`
            : baseUrl
    }, [])

    const handleEditReview = () => {
        setCloseDrawerOpen(false);
    };

    const handleCancelReview = () => {
        setCloseDrawerOpen(false); // Close the confirmation drawer
        setWasCancelReviewClicked(true);
        form.reset()
    };

    const handleDrawerSubmitComplete = () => {
        onClose();
        if (writeReviewButtonRef.current) {
            writeReviewButtonRef.current.focus();
        }
    };

    const handleDrawerCloseComplete = () => {
        if (wasCancelReviewClicked) {
            onClose();
            if (writeReviewButtonRef.current) {
                writeReviewButtonRef.current.focus();
            }
            setWasCancelReviewClicked(false); // Reset the state
        }
    };

    const hasAnyFilledValue = (value) => {
        if (typeof value === 'string') return value.trim().length > 0
        if (typeof value === 'number') return !Number.isNaN(value) && value > 0
        if (typeof value === 'boolean') return value === true
        if (Array.isArray(value)) return value.some((item) => hasAnyFilledValue(item))
        if (value && typeof value === 'object') {
            return Object.values(value).some((item) => hasAnyFilledValue(item))
        }
        return false
    }

    const hasFormChanged = () => {
        const values = form.getValues() || {}

        const agreedTermsFieldId = reviewFormData?.agreedtotermsandconditions?.Id
        const fitFieldId = reviewFormData?.rating_HowDoesItFit?.Id
        const ratingFieldId = reviewFormData?.rating?.Id

        return Object.entries(values).some(([key, value]) => {
            if (key === agreedTermsFieldId) return false

            if (key === fitFieldId) {
                const fitValue = Number(Array.isArray(value) ? value[0] : value)
                return Number.isFinite(fitValue) && fitValue !== 3
            }

            if (key === ratingFieldId) {
                return Number(value) > 0
            }

            return hasAnyFilledValue(value)
        })
    }

    const closeWriteReviewDrawer = () => {
        onClose()
        if (writeReviewButtonRef.current) {
            writeReviewButtonRef.current.focus()
        }
    }

    // Watch all form values reactively so the submit button enables only when
    // all required fields are filled (no validation-mode change needed).
    const formValues = form.watch()
    const isSubmitDisabled =
        !reviewFormData ||
        !(Number(formValues[reviewFormData?.rating?.Id]) > 0) ||
        !String(formValues[reviewFormData?.title?.Id] || '').trim() ||
        !String(formValues[reviewFormData?.reviewtext?.Id] || '').trim() ||
        !String(formValues[reviewFormData?.usernickname?.Id] || '').trim()

    const handleWriteReviewClose = () => {
        if (!hasFormChanged()) {
            closeWriteReviewDrawer()
            return
        }

        setCloseDrawerOpen(true)
        if (wasCancelReviewClicked) {
            closeWriteReviewDrawer()
            setWasCancelReviewClicked(false); // Reset the state
        }
    }

    const handleJoinNow = () => {
        if (typeof window !== 'undefined') {
            scrollPositionBeforeAuthRef.current = window.scrollY
            pdpPathBeforeAuthRef.current = `${window.location.pathname}${window.location.search}${window.location.hash}`
        }
        shouldResumeWriteReviewRef.current = true
        onClose()
        authModal.onOpen()
    }

    const handleAuthLoginSuccess = () => {
        if (!shouldResumeWriteReviewRef.current) {
            return
        }

        shouldResumeWriteReviewRef.current = false

        if (typeof window !== 'undefined') {
            const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
            if (pdpPathBeforeAuthRef.current && pdpPathBeforeAuthRef.current !== currentPath) {
                window.location.assign(pdpPathBeforeAuthRef.current)
                return
            }
        }

        onOpenWriteReview?.()
    }

    const handleAuthModalClose = () => {
        authModal.onClose()

        if (
            typeof window !== 'undefined' &&
            typeof scrollPositionBeforeAuthRef.current === 'number'
        ) {
            const scrollTop = scrollPositionBeforeAuthRef.current
            window.requestAnimationFrame(() => {
                window.scrollTo(0, scrollTop)
            })
        }
    }

    return (
        <>
            <Drawer
                placement={drawerPlacement}
                isOpen={isOpen}
                onClose={handleWriteReviewClose}
                onOpen={onOpen}
                size={{base: 'full', md: 'sm'}}
                motionPreset="slideInBottom"
                scrollBehavior="inside"
                isFullHeight={true}
            >
                <DrawerOverlay />
                <DrawerContent marginTop={0}>
                    <DrawerHeader padding={'16px 16px'}>
                        <Text as="h3" textStyle={'webH3'} mb={1}>
                            {isGarage ? (
                                <FormattedMessage
                                    id="pdp_reviews.write.review.text.garage"
                                    defaultMessage="Write your review"
                                />
                            ) : (
                                <FormattedMessage
                                    id="pdp_reviews.write.review.text.dynamite"
                                    defaultMessage="Write your review"
                                />
                            )}
                        </Text>
                        <DrawerCloseButton
                            w={{base: 6}}
                            h={{base: 6}}
                            top={{base: '12px', lg: '15px'}}
                            right={{base: '12px', lg: '28px'}}
                            zIndex={'2'}
                        />
                    </DrawerHeader>

                    <DrawerBody
                        ref={drawerBodyRef}
                        px={{base: 4, lg: 6}}
                        py={{base: 4}}
                        sx={{scrollbarGutter: {base: 'auto', lg: 'stable'}}}
                    >
                        <form onSubmit={form.handleSubmit(onSubmit, scrollToFirstError)}>
                            {/* Form */}
                            <WriteReviewFields
                                pid={pid}
                                form={form}
                                reviewFormData={reviewFormData}
                                product={product}
                                onJoinNow={handleJoinNow}
                            >
                                {images?.length > 0 && (
                                    <Box {...productThumbnailStyles.container}>
                                        <Image
                                            src={getImageUrl(images[0])}
                                            alt={images[0].alt}
                                            title={images[0]?.title}
                                            {...productThumbnailStyles.image}
                                        />
                                    </Box>
                                )}
                            </WriteReviewFields>

                            {/* Submit */}
                            <Box tabIndex={-1} ref={submitFocusSentinelRef} outline="none" />
                            <Button
                                variant="webPrimary"
                                type="submit"
                                title="Submit"
                                w={'100%'}
                                mt={8}
                                onPointerDown={moveFocusAwayFromSubmit}
                                isDisabled={isSubmitDisabled}
                                isLoading={form.formState.isSubmitting}
                                loadingText={formatMessage({
                                    defaultMessage: 'Submitting...',
                                    id: 'pdp_reviews.write.review.submitting'
                                })}
                            >
                                <FormattedMessage
                                    defaultMessage="Submit review"
                                    id="pdp_reviews.write.review.submit"
                                />
                            </Button>

                            {globalError && (
                                <Text textStyle={'bodyXsRegular'} color="webTypo.textError" mt={2}>
                                    {globalError}
                                </Text>
                            )}

                            <Text textStyle={'bodyXsRegular'} color="webBasic.950" mt={4}>
                                <FormattedMessage
                                    defaultMessage="All submitted reviews are subject to the terms set forth in our website"
                                    id="pdp_reviews.terms.notice"
                                />
                            </Text>
                        </form>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            {/* Auth modal */}
            <AuthModal
                {...authModal}
                onClose={handleAuthModalClose}
                onLoginSuccess={handleAuthLoginSuccess}
                returnFocusOnClose={false}
            />

            {/* Review Submitted Confirmation Drawer */}
            <Drawer
                placement={drawerPlacement}
                isOpen={isReviewSubmittedOpen}
                onClose={() => setReviewSubmittedOpen(false)}
                onOpen={isReviewSubmittedOpen}
                onCloseComplete={handleDrawerSubmitComplete}
                size={{base: 'full', md: 'sm'}}
                motionPreset="slideInBottom"
                scrollBehavior="inside"
                isFullHeight={true}
                autoFocus={false}
                returnFocusOnClose={false}
            >
                <DrawerOverlay />
                <DrawerContent marginTop={0}>
                    <DrawerHeader padding={'16px 16px'}>
                        <Text as="h3" textStyle={'webH3'} mX={'2px'}>
                            {isGarage ? (
                                <FormattedMessage
                                    id="pdp_reviews.write.review.text.garage"
                                    defaultMessage="Write your review"
                                />
                            ) : (
                                <FormattedMessage
                                    id="pdp_reviews.write.review.text.dynamite"
                                    defaultMessage="Write your review"
                                />
                            )}
                        </Text>
                        <DrawerCloseButton
                            w={{base: 6}}
                            h={{base: 6}}
                            top={{base: '12px', lg: '15px'}}
                            right={{base: '12px', lg: '28px'}}
                            zIndex={'2'}
                        />
                    </DrawerHeader>

                    <DrawerBody
                        px={{base: 4, lg: 6}}
                        py={{base: 6}}
                        sx={{scrollbarGutter: {base: 'auto', lg: 'stable'}}}
                        display="flex"
                        flexDirection="column"
                    >
                        <Box flex="1">
                            <Flex alignItems="center" gap={1}>
                                <Icon
                                    as={CheckCircleIcon}
                                    width="24px"
                                    height="24px"
                                    flexShrink={0}
                                />

                                <Text as="h3" textStyle="webH3">
                                    {isGarage ? (
                                        <FormattedMessage
                                            defaultMessage="YOUR REVIEW WAS SUBMITTED SUCCESSFULLY!"
                                            id="pdp_reviews.review.submitted.successfully.garage"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="YOUR REVIEW WAS SUBMITTED SUCCESSFULLY!"
                                            id="pdp_reviews.review.submitted.successfully.dynamite"
                                        />
                                    )}
                                </Text>
                            </Flex>

                            <Text textStyle="bodySmRegular" mt={{base: 6}}>
                                {isGarage ? (
                                    <FormattedMessage
                                        defaultMessage="Please note that it may take up to 72 hours for your review to appear."
                                        id="pdp_reviews.review.submitted.description.garage"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Please note that it may take up to 72 hours for your review to appear."
                                        id="pdp_reviews.review.submitted.description.dynamite"
                                    />
                                )}
                            </Text>
                        </Box>
                    </DrawerBody>
                    <DrawerFooter
                        borderTop={'1px solid'}
                        borderColor={'webTheme.strokeTertiary'}
                        position={{base: 'sticky'}}
                        bottom={{base: 'env(safe-area-inset-bottom, 0px)', lg: 0}}
                        left={0}
                        right={0}
                        pt={4}
                        px={4}
                        pb={{base: 'calc(16px + env(safe-area-inset-bottom, 0px))', lg: 4}}
                        bgColor="webTheme.backgroundDefault"
                        zIndex={2}
                    >
                        <Button
                            variant="webPrimary"
                            onClick={() => setReviewSubmittedOpen(false)}
                            w="100%"
                        >
                            <FormattedMessage
                                defaultMessage="Continue Shopping"
                                id="pdp_reviews.continue_shopping.button"
                            />
                        </Button>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>

            {/* Close Drawer Confirmation */}
            <Drawer
                placement={drawerPlacement}
                isOpen={isCloseDrawerOpen}
                onClose={() => setCloseDrawerOpen(false)}
                size={{base: 'sm', md: 'sm'}}
                motionPreset="slideInBottom"
                scrollBehavior="inside"
                isFullHeight={drawerPlacement === 'right'}
                onCloseComplete={handleDrawerCloseComplete}
            >
                <DrawerOverlay />
                <DrawerContent marginTop={0}>
                    <DrawerCloseButton
                        w={{base: 6}}
                        h={{base: 6}}
                        top={{base: '32px', lg: '26px'}}
                        right={{base: '16px', lg: '34px'}}
                        zIndex={'2'}
                    />
                    <DrawerBody
                        px={{base: 4, lg: 6}}
                        py={{base: 4}}
                        sx={{scrollbarGutter: {base: 'auto', lg: 'stable'}}}
                    >
                        <Text
                            as="h3"
                            textStyle={'webH3'}
                            mb={1}
                            mt={{base: '32px', md: '64px'}}
                            pt={{base: '24px'}}
                        >
                            {isGarage ? (
                                <FormattedMessage
                                    defaultMessage="ARE YOU SURE YOU WANT TO QUIT?"
                                    id="pdp_reviews.quit.review.question.garage"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="ARE YOU SURE YOU WANT TO QUIT?"
                                    id="pdp_reviews.quit.review.question.dynamite"
                                />
                            )}
                        </Text>

                        <Text textStyle="bodySmRegular" mt={{base: 2}}>
                            {isGarage ? (
                                <FormattedMessage
                                    defaultMessage="Are you sure you want to cancel the review process? Your progress will not be saved."
                                    id="pdp_reviews.quit.review.description.garage"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Are you sure you want to cancel the review process? Your progress will not be saved."
                                    id="pdp_reviews.quit.review.description.dynamite"
                                />
                            )}
                        </Text>

                        <Flex gap={2} direction={'column'} mt={{base: '48px'}} mb={{base: '24px'}}>
                            <Button type="button" variant="webPrimary" onClick={handleCancelReview}>
                                <FormattedMessage
                                    defaultMessage="CANCEL REVIEW"
                                    id="pdp_reviews.cancel.review.button"
                                />
                            </Button>
                            <Button
                                type="button"
                                variant="webSecondaryOutline"
                                onClick={handleEditReview}
                            >
                                <FormattedMessage
                                    defaultMessage="EDIT MY REVIEW"
                                    id="pdp_reviews.edit.review.button"
                                />
                            </Button>
                        </Flex>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    )
};

WriteReview.propTypes = {
    product: PropTypes.object.isRequired,
    reviewFormData: PropTypes.object,
    images: PropTypes.array,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onOpenWriteReview: PropTypes.func
};

export default WriteReview;
